import { TourEvent } from '@/domain/project-tour-ontour/entities/project-tour-ontour-event.entity';
import {
  TourTravel,
  TourTravelConnexionTypeWithEvent,
} from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import { translate } from '@/ui/i18n';
import { formatDate } from '@/ui/utils/format-date';

export type FavoriteTravelGroupOption = {
  key: string;
  label: string;
  travels: TourTravel[];
};

export function buildFavoriteGroupKey(
  connexionType: TourTravelConnexionTypeWithEvent,
  anchorEventId: number,
): string {
  return `${connexionType}:${anchorEventId}`;
}

function parseFavoriteGroupKey(key: string): {
  connexionType: TourTravelConnexionTypeWithEvent;
  anchorEventId: number;
} {
  const [connexionType, anchorEventId] = key.split(':');
  return {
    connexionType: connexionType as TourTravelConnexionTypeWithEvent,
    anchorEventId: Number(anchorEventId),
  };
}

export function getTravelsOfWindow(
  allTravels: TourTravel[],
  connexionType: TourTravelConnexionTypeWithEvent,
  anchorEventId: number,
): TourTravel[] {
  return allTravels.filter((travel) => {
    if (travel.connexionTypeWithEvent !== connexionType) {
      return false;
    }
    switch (connexionType) {
      case 'incoming':
        return travel.toEventId === anchorEventId;
      case 'outgoing':
      case 'direct':
        return travel.fromEventId === anchorEventId;
    }
  });
}

function buildGroupLabel(
  connexionType: TourTravelConnexionTypeWithEvent,
  anchorEventId: number,
  tourEvents: TourEvent[],
): string {
  const typeLabel = translate(`ProjectsTour.travel-${connexionType}-button`);
  const event = tourEvents.find((item) => item.id === anchorEventId);
  const eventName = event?.position?.properties.name ?? event?.name ?? '';
  const eventDate = event?.date ? formatDate(event.date) : '';
  const eventLabel = [eventName, eventDate].filter(Boolean).join(' ');
  return [typeLabel, eventLabel].filter(Boolean).join(' · ');
}

function isConnexionCompatible(
  a: TourTravelConnexionTypeWithEvent,
  b: TourTravelConnexionTypeWithEvent,
): boolean {
  if (a === 'direct' || b === 'direct') {
    return a === b;
  }
  return true;
}

export function buildFavoriteGroupOptions({
  favoriteGroupKeys,
  allTravels,
  tourEvents,
  targetConnexionType,
  excludeKey,
}: {
  favoriteGroupKeys: Set<string>;
  allTravels: TourTravel[];
  tourEvents: TourEvent[];
  targetConnexionType: TourTravelConnexionTypeWithEvent;
  excludeKey: string;
}): FavoriteTravelGroupOption[] {
  const options: FavoriteTravelGroupOption[] = [];
  for (const key of favoriteGroupKeys) {
    if (key === excludeKey) {
      continue;
    }
    const { connexionType, anchorEventId } = parseFavoriteGroupKey(key);
    if (!isConnexionCompatible(connexionType, targetConnexionType)) {
      continue;
    }
    const travels = getTravelsOfWindow(
      allTravels,
      connexionType,
      anchorEventId,
    );
    if (travels.length === 0) {
      continue;
    }
    options.push({
      key,
      label: buildGroupLabel(connexionType, anchorEventId, tourEvents),
      travels,
    });
  }
  return options;
}
